import React, { useState, useEffect } from "react";
import axios from "axios";
import { evaluateFDP, utcToLocal } from "../far117/far117Engine";
import { findCriticalLeg, calculateDelayMargin } from "../far117/helpers";
import { useFDPClock } from "../far117/useFDPClock";
import Far117LimitsCard from "./Far117LimitsCard";

/* ─────────────────────────────────────────────────────────────────────────
   Formatting helpers
───────────────────────────────────────────────────────────────────────── */
const pad     = (n) => String(n).padStart(2, "0");
const hhmm    = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** Display a UTC Date as HH:MM in a given offset (defaults 0 = UTC) */
const fmt24 = (date, offsetHours = 0) => {
  if (!date || isNaN(date)) return "--";
  return utcToLocal(date, offsetHours);
};

const fmtMins = (min) => {
  if (min == null) return "--";
  const h = Math.floor(Math.abs(min) / 60);
  const m = Math.abs(min) % 60;
  return h > 0 ? `${h}h ${pad(m)}m` : `${m}m`;
};

/** Friendly UTC offset label: "+8:00", "-5:30", "UTC" */
const fmtOffset = (hours) => {
  if (hours == null) return "";
  if (hours === 0) return "UTC";
  const sign = hours > 0 ? "+" : "-";
  const abs  = Math.abs(hours);
  const h    = Math.floor(abs);
  const m    = Math.round((abs - h) * 60);
  return `UTC${sign}${h}${m ? `:${pad(m)}` : ""}`;
};

/* ─────────────────────────────────────────────────────────────────────────
   Supported acclimated timezones — IANA id + display label.
   Offsets are resolved dynamically (DST-aware) at render time.
───────────────────────────────────────────────────────────────────────── */
const ACCLIMATED_ZONES = [
  { id: "America/Anchorage",   label: "Alaska (ANC/FAI)"           },
  { id: "America/Los_Angeles", label: "Pacific (LAX/SEA/PDX/SFO)"  },
  { id: "America/Denver",      label: "Mountain (DEN/SLC/BOI)"     },
  { id: "America/Phoenix",     label: "Arizona / no DST (PHX)"     },
  { id: "America/Chicago",     label: "Central (ORD/DFW/MSP)"      },
  { id: "America/New_York",    label: "Eastern (JFK/MIA/BOS)"      },
  { id: "America/Honolulu",    label: "Hawaii (HNL/OGG)"           },
  { id: "Europe/London",       label: "UK (LHR/LGW)"               },
  { id: "Europe/Paris",        label: "Central Europe (CDG/FRA)"   },
  { id: "Asia/Tokyo",          label: "Japan (NRT/HND)"            },
  { id: "Asia/Dubai",          label: "Gulf (DXB/AUH)"             },
  { id: "Australia/Sydney",    label: "Sydney (SYD)"               },
  { id: "Pacific/Auckland",    label: "New Zealand (AKL)"          },
];

/* ─────────────────────────────────────────────────────────────────────────
   Auth header — matches EditFlight.js pattern exactly
───────────────────────────────────────────────────────────────────────── */
const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
};

/* ─────────────────────────────────────────────────────────────────────────
   DST-aware timezone offset — ported directly from EditFlight.js.
   Returns the UTC offset in hours (e.g. -8 for PST, -7 for PDT).
───────────────────────────────────────────────────────────────────────── */
const getNthSundayOfMonth = (year, month, n) => {
  const firstDay    = new Date(year, month, 1);
  const firstSunday = new Date(firstDay);
  firstSunday.setDate(1 + (7 - firstDay.getDay()) % 7);
  const nthSunday = new Date(firstSunday);
  nthSunday.setDate(firstSunday.getDate() + (n - 1) * 7);
  return nthSunday;
};

const getTimezoneOffset = (timezoneId, date) => {
  try {
    if (!timezoneId) return 0;
    // Method 1: Intl.DateTimeFormat (most reliable, handles DST automatically)
    try {
      const utcDate = new Date(`${date}T12:00:00.000Z`);
      const fmt = new Intl.DateTimeFormat("en", {
        timeZone: timezoneId,
        timeZoneName: "longOffset",
      });
      const parts      = fmt.formatToParts(utcDate);
      const offsetPart = parts.find((p) => p.type === "timeZoneName");
      if (offsetPart && offsetPart.value) {
        const match = offsetPart.value.match(/GMT([+-])(\d{1,2}):?(\d{2})?/);
        if (match) {
          const sign    = match[1] === "+" ? 1 : -1;
          const hours   = parseInt(match[2]);
          const minutes = parseInt(match[3] || "0");
          return sign * (hours + minutes / 60);
        }
      }
    } catch (_) {}
    // Method 2: toLocaleString UTC diff
    try {
      const utcDate    = new Date(`${date}T12:00:00.000Z`);
      const targetDate = new Date(utcDate.toLocaleString("en-US", { timeZone: timezoneId }));
      return (utcDate.getTime() - targetDate.getTime()) / 3600000;
    } catch (_) {}
    // Method 3: Hardcoded US offsets with DST calculation
    const baseOffsets = {
      "America/New_York":    -5,
      "America/Chicago":     -6,
      "America/Denver":      -7,
      "America/Los_Angeles": -8,
      "America/Anchorage":   -9,
      "Pacific/Honolulu":    -10,
    };
    if (baseOffsets[timezoneId] != null) {
      const year     = new Date(date).getFullYear();
      const testDate = new Date(date);
      const dstStart = getNthSundayOfMonth(year, 2, 2);
      const dstEnd   = getNthSundayOfMonth(year, 10, 1);
      const isDST    = testDate >= dstStart && testDate < dstEnd;
      const base     = baseOffsets[timezoneId];
      return base + (isDST && timezoneId !== "Pacific/Honolulu" ? 1 : 0);
    }
    return 0;
  } catch (error) {
    console.error("Error calculating timezone offset:", error, "for timezone:", timezoneId);
    return 0;
  }
};

/* ─────────────────────────────────────────────────────────────────────────
   Airport lookup — axios + authToken, DST-aware offset resolved from
   time_zone column, with utc_offset_hours as fallback. Matches EditFlight.
───────────────────────────────────────────────────────────────────────── */
async function fetchAirport(code, date = null) {
  if (!code || code.length < 3) return null;
  try {
    const response = await axios.get(
      `/api/airports/${code.toUpperCase()}`,
      getAuthHeaders()
    );
    const airport = response.data;
    if (!airport) return null;

    // Resolve DST-aware offset from time_zone, fall back to static utc_offset_hours
    const flightDate = date || new Date().toISOString().split("T")[0];
    let resolvedOffset;
    if (airport.time_zone) {
      resolvedOffset = getTimezoneOffset(airport.time_zone, flightDate);
    } else if (airport.utc_offset_hours != null) {
      console.warn(`No time_zone for ${code}, using static offset: ${airport.utc_offset_hours}`);
      resolvedOffset = parseFloat(airport.utc_offset_hours);
    } else {
      resolvedOffset = 0;
    }

    // Return airport with utc_offset_hours overridden by the DST-correct value
    return { ...airport, utc_offset_hours: resolvedOffset };
  } catch (error) {
    if (error.response && error.response.status === 404) return null;
    console.error("Error fetching airport data:", error);
    return null;
  }
}

/* ─────────────────────────────────────────────────────────────────────────
   Default segments
───────────────────────────────────────────────────────────────────────── */
function makeDefaultSegments() {
  const blank = { depAirport: null, arrAirport: null, depCode: "", arrCode: "", depError: false, arrError: false, blockInput: "", dep: "", arr: "", arrCalc: false, depCalc: false };
  return [ { ...blank } ];
}

/* ─────────────────────────────────────────────────────────────────────────
   AirportTag — small pill shown under an airport code input
───────────────────────────────────────────────────────────────────────── */
function AirportTag({ airport, error }) {
  if (error) return <span className="efb-airport-tag error">Not found</span>;
  if (!airport) return null;
  const label = [airport.name, fmtOffset(airport.utc_offset_hours)]
    .filter(Boolean).join(" · ");
  return <span className="efb-airport-tag">{label}</span>;
}

/* ─────────────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────────────── */
export default function EFBPanel() {
  const [dutyDate, setDutyDate]   = useState("");   // "YYYY-MM-DD"
  const [dutyTime, setDutyTime]   = useState("");   // "HH:MM" 24hr text
  // Convenience: combined string used by downstream effects
  const dutyStartInput = (dutyDate && dutyTime) ? `${dutyDate}T${dutyTime}` : "";
  const [pilots, setPilots]     = useState(2);
  const [acclimatedZone, setAcclimatedZone] = useState(""); // IANA tz id, "" = use dep airport
  const [segments, setSegments] = useState(makeDefaultSegments());
  const [result, setResult]     = useState(null);

  // Append 'Z' so JS treats the datetime-local value as UTC, not browser-local time.
  // The airport UTC offset in the engine converts it to true UTC from the pilot's local clock.
  // Convert the user's entered local time to true UTC by subtracting the
  // active offset (acclimated zone if set, otherwise first dep airport).
  // This ensures dutyStart is always real UTC throughout the engine.
  const reportOffsetForParsing = (() => {
    if (!dutyStartInput) return 0;
    if (acclimatedZone) {
      const dateStr = dutyStartInput.split("T")[0];
      return getTimezoneOffset(acclimatedZone, dateStr);
    }
    return segments[0]?.depAirport?.utc_offset_hours ?? 0;
  })();

  const dutyStart = (() => {
    if (!dutyStartInput) return null;
    // Parse the raw string as UTC-midnight-anchored, then subtract offset
    // so the stored Date is true UTC: local 13:00 UTC-5 → stored as 18:00 UTC
    const raw = new Date(dutyStartInput + ":00Z");
    if (isNaN(raw.getTime())) return null;
    return new Date(raw.getTime() - reportOffsetForParsing * 3_600_000);
  })();
  const validDutyStart = dutyStart && !isNaN(dutyStart.getTime()) ? dutyStart : null;

  /* Auto-recalculate on any change */
  useEffect(() => {
    if (!validDutyStart) { setResult(null); return; }

    // Only run the engine if every leg has valid dep + arr times.
    // A leg is "ready" when both HH:MM fields are complete (5 chars with colon).
    // Incomplete legs produce NaN in the engine which cascades to illegal/NaN UI.
    const isValidTime = (t) => t && t.length === 5 && t.includes(":");
    const allLegsReady = segments.every(seg => isValidTime(seg.dep) && isValidTime(seg.arr));
    if (!allLegsReady) { setResult(null); return; }

    // Resolve acclimated timezone offset for the duty date (DST-aware)
    let acclimatedOffset = null;
    if (acclimatedZone) {
      const dateStr = dutyStartInput.split("T")[0];
      acclimatedOffset = getTimezoneOffset(acclimatedZone, dateStr);
    }
    setResult(evaluateFDP({ dutyStart: validDutyStart, segments, pilots, acclimatedOffset }));
  }, [dutyDate, dutyTime, segments, pilots]); // eslint-disable-line

  /* ── duty start handlers ── */
  const handleDutyDateChange = (e) => {
    const val = e.target.value;
    setDutyDate(val);
    // Seed default leg times on first date entry if time is already set
    if (val && dutyTime && !dutyDate) setSegments(makeDefaultSegments());
    setResult(null);
  };

  const handleDutyTimeChange = (e) => {
    // Strip any non-digit/colon chars; enforce HH:MM format
    let val = e.target.value.replace(/[^0-9:]/g, "").slice(0, 5);
    // Auto-insert colon after 2 digits
    if (val.length === 2 && !val.includes(":")) val = val + ":";
    setDutyTime(val);
    if (dutyDate && val.length === 5 && !dutyStartInput) {
      setSegments(makeDefaultSegments());
    }
    setResult(null);
  };

  /* ── airport lookup on blur ── */
  const handleAirportBlur = async (segIndex, field, code) => {
    // field is "depCode" or "arrCode"
    const airportField = field === "depCode" ? "depAirport" : "arrAirport";
    const errorField   = field === "depCode" ? "depError"   : "arrError";

    if (!code || code.trim().length < 3) {
      // Clear airport data if input is blanked
      setSegments((prev) => prev.map((seg, i) =>
        i === segIndex ? { ...seg, [airportField]: null, [errorField]: false } : seg
      ));
      return;
    }

    // Pass duty date so DST offset is calculated for the correct day
    const lookupDate = dutyStartInput ? dutyStartInput.split("T")[0] : null;
    const airport = await fetchAirport(code.trim(), lookupDate);

    setSegments((prev) => prev.map((seg, i) => {
      if (i !== segIndex) return seg;
      const withAirport = { ...seg, [airportField]: airport, [errorField]: !airport };
      // Re-run solver so block/arr recalculates with the new timezone offset
      return solveSegment(withAirport, airportField, airport);
    }));
  };

  /* ── segment field updates ── */
  /* ── Smart three-field solver: dep + block → arr (priority)
         dep + arr  → block
         arr + block → dep
     When all three are filled and one changes, arrival is recalculated
     (lowest priority). blockInput stores HH:MM typed by user; the engine
     always receives dep + arr strings. ── */
  const solveSegment = (seg, changed, value) => {
    const updated = { ...seg, [changed]: value };

    const parseHHMM = (str) => {
      if (!str || str.length < 4 || !str.includes(":")) return null;
      const [h, m] = str.split(":").map(Number);
      if (isNaN(h) || isNaN(m) || m > 59 || h > 23) return null;
      return h * 60 + m;
    };

    const toHHMM = (totalMin) => {
      const wrapped = ((totalMin % 1440) + 1440) % 1440;
      return `${pad(Math.floor(wrapped / 60))}:${pad(wrapped % 60)}`;
    };

    // tzDelta (minutes): how much further east dep is than arr.
    // KMKE UTC-5, KSEA UTC-7 → tzDelta = (-5 - -7) * 60 = +120 min.
    // arr_local = dep_local + utcBlock - tzDelta
    // utcBlock  = arr_local - dep_local + tzDelta
    const depOffset = updated.depAirport?.utc_offset_hours ?? null;
    const arrOffset = updated.arrAirport?.utc_offset_hours ?? null;
    const tzDelta   = (depOffset !== null && arrOffset !== null)
      ? (depOffset - arrOffset) * 60
      : 0;

    const depMin   = parseHHMM(updated.dep);
    const arrMin   = parseHHMM(updated.arr);
    const blockMin = parseHHMM(updated.blockInput); // UTC block time

    // Priority rules — arrival is always the lowest-priority output:
    //   dep changed   → keep block, recalculate arr
    //                   (fallback: keep arr, recalculate block)
    //   block changed → keep dep, recalculate arr
    //                   (fallback: keep arr, recalculate dep)
    //   arr changed   → keep dep+block, recalculate block
    //                   (fallback: keep block, recalculate dep)
    //
    // This means you can freely edit any field at any time and the
    // other two will update without needing to clear anything first.

    if (changed === "dep") {
      if (depMin !== null && blockMin !== null) {
        // dep + block → arr  (always preferred when dep changes)
        updated.arr = toHHMM(depMin + blockMin - tzDelta);
        updated.arrCalc = true;
        updated.depCalc = false;
      } else if (depMin !== null && arrMin !== null) {
        // dep + arr → block  (no block entered yet)
        updated.blockInput = toHHMM(arrMin - depMin + tzDelta);
        updated.arrCalc = false;
        updated.depCalc = false;
      } else {
        updated.arrCalc = false;
        updated.depCalc = false;
      }

    } else if (changed === "blockInput") {
      if (depMin !== null && blockMin !== null) {
        // dep + block → arr  (always preferred when block changes)
        updated.arr = toHHMM(depMin + blockMin - tzDelta);
        updated.arrCalc = true;
        updated.depCalc = false;
      } else if (arrMin !== null && blockMin !== null) {
        // arr + block → dep  (no dep entered)
        updated.dep = toHHMM(arrMin - blockMin + tzDelta);
        updated.depCalc = true;
        updated.arrCalc = false;
      } else {
        updated.arrCalc = false;
        updated.depCalc = false;
      }

    } else if (changed === "arr") {
      if (depMin !== null && arrMin !== null) {
        // dep + arr → block  (always preferred when arr changes)
        updated.blockInput = toHHMM(arrMin - depMin + tzDelta);
        updated.arrCalc = false;
        updated.depCalc = false;
      } else if (arrMin !== null && blockMin !== null) {
        // arr + block → dep  (no dep entered)
        updated.dep = toHHMM(arrMin - blockMin + tzDelta);
        updated.depCalc = true;
        updated.arrCalc = false;
      } else {
        updated.arrCalc = false;
        updated.depCalc = false;
      }

    } else if (changed === "depAirport" || changed === "arrAirport") {
      // Airport resolved — recompute with updated tzDelta
      const dOff  = updated.depAirport?.utc_offset_hours ?? null;
      const aOff  = updated.arrAirport?.utc_offset_hours ?? null;
      const delta = (dOff !== null && aOff !== null) ? (dOff - aOff) * 60 : 0;
      if (depMin !== null && blockMin !== null) {
        // dep + block → arr  (preferred)
        updated.arr = toHHMM(depMin + blockMin - delta);
        updated.arrCalc = true;
        updated.depCalc = false;
      } else if (depMin !== null && arrMin !== null) {
        // dep + arr → block
        updated.blockInput = toHHMM(arrMin - depMin + delta);
        updated.arrCalc = false;
        updated.depCalc = false;
      }
    }

    return updated;
  };

  const updateSegment = (index, field, value) =>
    setSegments((prev) =>
      prev.map((seg, i) => (i === index ? solveSegment(seg, field, value) : seg))
    );

  /* ── add / remove legs ── */
  const addLeg = () =>
    setSegments((prev) => {
      const last = prev[prev.length - 1];
      const seed = (base, offsetMin) => {
        if (!base) return "";
        const [h, m] = base.split(":").map(Number);
        const d = new Date(validDutyStart || Date.now());
        d.setHours(h, m + offsetMin, 0, 0);
        return hhmm(d);
      };
      const newDep = seed(last.arr, 45);
      const newArr = seed(last.arr, 105);
      // Pre-seed blockInput from the seeded dep/arr times
      const seedBlock = (() => {
        if (!newDep || !newArr) return "";
        const [dh, dm] = newDep.split(":").map(Number);
        const [ah, am] = newArr.split(":").map(Number);
        const diff = ((ah * 60 + am) - (dh * 60 + dm) + 1440) % 1440;
        return `${pad(Math.floor(diff / 60))}:${pad(diff % 60)}`;
      })();
      return [...prev, {
        dep: newDep, arr: newArr, blockInput: seedBlock,
        arrCalc: false, depCalc: false,
        // Carry the last arrival airport forward as the next departure airport
        depCode: last.arrCode || "", arrCode: "",
        depAirport: last.arrAirport || null, arrAirport: null,
        depError: false, arrError: false,
      }];
    });

  const removeLeg = (index) =>
    setSegments((prev) => prev.filter((_, i) => i !== index));

  /* ── derived display values ── */
  // Pass the engine's pre-computed dutyEndLimit — single source of truth
  const clock = useFDPClock(result?.limits.dutyEndLimit ?? null);

  const critical = result
    ? findCriticalLeg({ timeline: result.timeline, dutyEndLimit: result.limits.dutyEndLimit })
    : null;

  const margin = result
    ? calculateDelayMargin({ timeline: result.timeline, dutyEndLimit: result.limits.dutyEndLimit })
    : null;

  const turnMins = result
    ? result.timeline.map((leg, i) =>
        i < result.timeline.length - 1
          ? Math.round((result.timeline[i + 1].depUTC - leg.arrUTC) / 60_000)
          : null
      )
    : segments.map(() => null);

  const isLegal    = result?.overallLegal;
  const hasResult  = result !== null;
  const baseOffset = result?.baseOffset ?? 0;

  // Preview FDP limit + end when result is null (legs incomplete) but we have
  // a valid report time — computed from the same Table B logic as the engine.
  const TABLE_B_PREVIEW = [
    {s:0,   e:359,  v:[9,9,9,9,9,9,9]},
    {s:400, e:459,  v:[10,10,10,10,9,9,9]},
    {s:500, e:559,  v:[12,12,12,12,11.5,11,10.5]},
    {s:600, e:659,  v:[13,13,12,12,11.5,11,10.5]},
    {s:700, e:1159, v:[14,14,13,13,12.5,12,11.5]},
    {s:1200,e:1259, v:[13,13,13,13,12.5,12,11.5]},
    {s:1300,e:1659, v:[12,12,12,12,11.5,11,10.5]},
    {s:1700,e:2159, v:[12,12,11,11,10,9,9]},
    {s:2200,e:2259, v:[11,11,10,10,9,9,9]},
    {s:2300,e:2359, v:[10,10,10,9,9,9,9]},
  ];
  const previewData = (() => {
    if (hasResult || !validDutyStart) return null;

    // Only compute a preview when we have a real offset to work from:
    // either an acclimated zone is selected, or the first leg's departure
    // airport has been looked up. Falling back to UTC (0) would give the
    // wrong Table B row and a misleading FDP limit.
    const hasAcclimated = Boolean(acclimatedZone);
    const hasDepAirport = segments[0]?.depAirport?.utc_offset_hours != null;
    if (!hasAcclimated && !hasDepAirport) return null;

    const depOffset = acclimatedZone
      ? getTimezoneOffset(acclimatedZone, dutyStartInput.split("T")[0])
      : segments[0].depAirport.utc_offset_hours;

    const localDate = new Date(validDutyStart.getTime() + depOffset * 3_600_000);
    const t = localDate.getUTCHours() * 100 + localDate.getUTCMinutes();
    const row = TABLE_B_PREVIEW.find(r => t >= r.s && t <= r.e);
    const fdpLimit = row ? row.v[Math.min(segments.length, 7) - 1] : 9;
    const dutyEnd  = new Date(validDutyStart.getTime() + fdpLimit * 3_600_000);
    return { fdpLimit, dutyEnd, depOffset };
  })();

  // Label shown in the UI for the active acclimated zone
  const acclimatedZoneLabel = acclimatedZone
    ? (ACCLIMATED_ZONES.find(z => z.id === acclimatedZone)?.label ?? acclimatedZone)
    : null;
  const acclimatedOffsetLabel = acclimatedZone && validDutyStart
    ? fmtOffset(getTimezoneOffset(acclimatedZone, dutyStartInput.split("T")[0]))
    : null;

  const clockPct = clock && result
    ? Math.max(0, Math.min(100, (clock.remainingMin / (result.limits.fdpLimit * 60)) * 100))
    : 0;

  const maxBlockMin = result
    ? Math.max(...result.timeline.map((l) => l.blockMin), 1)
    : 120;

  /* ── report time in local (base airport) ── */
  const reportLocal = validDutyStart
    ? fmt24(validDutyStart, baseOffset)
    : "--";
  const reportOffsetLabel = hasResult ? fmtOffset(baseOffset) : "";

  /* ─────────────────────────────────────────────────────────────────────
     Render
  ──────────────────────────────────────────────────────────────────────── */
  return (
    <>
      <style>{`
        /* .efb-tw holds Tailwind-styled components (the FAR 117 limits card) — keep their spacing */
        /* (:where keeps the reset as weak as plain ".efb *", so the page's own rules still win) */
        .efb :where(*:not(.efb-tw, .efb-tw *)) { box-sizing: border-box; margin: 0; padding: 0; }
        .efb {
          font-family: 'Manrope', -apple-system, BlinkMacSystemFont, sans-serif;
          background: #f8fafc;
          min-height: 100vh;
          color: #0f172a;
          padding: 28px 28px 48px;
        }

        /* ── HEADER ── */
        .efb-header {
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 12px; margin-bottom: 28px;
        }
        .efb-header h1 { font-size: 22px; font-weight: 700; color: #0f172a; letter-spacing: -0.025em; }
        .efb-header p  { font-size: 13px; color: #64748b; margin-top: 3px; }
        .efb-badge {
          font-family: 'JetBrains Mono', monospace;
          font-size: 13px; font-weight: 600; letter-spacing: 0.04em;
          padding: 6px 16px; border-radius: 6px;
        }
        .efb-badge.legal   { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .efb-badge.illegal { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .efb-badge.unset   { background: #f1f5f9; color: #64748b;  border: 1px solid #e2e8f0; }

        /* ── LAYOUT ── */
        .efb-layout {
          display: grid; grid-template-columns: 320px 1fr;
          gap: 24px; align-items: start;
        }
        /* FAR 117 limits card: under the inputs on wide screens (the results
           span both rows beside them), after the results on phones */
        .efb-layout > .efb-results { grid-column: 2; grid-row: 1 / span 2; }
        .efb-layout > .efb-limits  { grid-column: 1; grid-row: 2; font-family: Inter, system-ui, sans-serif; }
        @media (max-width: 860px) {
          .efb-layout { grid-template-columns: 1fr; }
          .efb-layout > .efb-results, .efb-layout > .efb-limits { grid-column: auto; grid-row: auto; }
          .efb { padding: 18px 16px 48px; }
          .efb-header h1 { font-size: 18px; }
        }

        /* ── CARD ── */
        .efb-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 22px; }
        .efb-card + .efb-card { margin-top: 16px; }
        .efb-card-title {
          font-size: 11px; font-weight: 700; letter-spacing: 0.09em;
          text-transform: uppercase; color: #94a3b8; margin-bottom: 16px;
        }

        /* ── FORM ── */
        .efb-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
        .efb-field:last-of-type { margin-bottom: 0; }
        .efb-field label { font-size: 13px; font-weight: 600; color: #374151; }
        .efb-hint { font-size: 11px; color: #94a3b8; margin-top: 1px; }
        .efb-acclim-note {
          font-size: 11px; color: #b45309; background: #fffbeb;
          border: 1px solid #fde68a; border-radius: 6px;
          padding: 6px 10px; margin-top: 6px; line-height: 1.4;
        }
        .efb-control {
          background: #f8fafc; border: 1.5px solid #e2e8f0; color: #0f172a;
          padding: 10px 14px; border-radius: 9px; font-size: 15px;
          font-family: 'Manrope', sans-serif; outline: none; width: 100%;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .efb-control:focus { border-color: #2563eb; box-shadow: 0 0 0 3px #dbeafe; background: #fff; }
        .efb-control-mono { font-family: 'JetBrains Mono', monospace; font-size: 18px; font-weight: 600; letter-spacing: 0.08em; }

        /* ── CREW TOGGLE ── */
        .efb-crew-toggle { display: flex; border: 1.5px solid #e2e8f0; border-radius: 9px; overflow: hidden; }
        .efb-crew-btn {
          flex: 1; padding: 10px 8px; font-size: 14px; font-weight: 600;
          font-family: 'Manrope', sans-serif; border: none; cursor: pointer;
          background: transparent; color: #64748b; transition: background 0.15s, color 0.15s;
        }
        .efb-crew-btn.active { background: #2563eb; color: #fff; }
        .efb-crew-btn:first-child { border-right: 1.5px solid #e2e8f0; }

        /* ── SUMMARY TABLE ── */
        .efb-summary-table { width: 100%; border-collapse: collapse; }
        .efb-summary-table tr { border-top: 1px solid #f1f5f9; }
        .efb-summary-table tr:first-child { border-top: none; }
        .efb-summary-table td { padding: 9px 0; vertical-align: middle; }
        .efb-summary-table td:first-child { font-size: 13px; font-weight: 600; color: #64748b; }
        .efb-summary-table td:last-child {
          text-align: right; font-family: 'JetBrains Mono', monospace;
          font-size: 14px; font-weight: 500; color: #0f172a;
        }
        .efb-summary-table td.sky   { color: #2563eb; }
        .efb-summary-table td.green { color: #15803d; }
        .efb-summary-table td.red   { color: #dc2626; }

        /* ── STAT CARDS ── */
        .efb-stats { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; margin-bottom: 18px; }
        @media (max-width: 600px) { .efb-stats { grid-template-columns: repeat(2,1fr); } }
        .efb-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px 18px; }
        .efb-stat-label {
          font-size: 11px; font-weight: 700; letter-spacing: 0.08em;
          text-transform: uppercase; color: #94a3b8; margin-bottom: 8px;
        }
        .efb-stat-val { font-family: 'JetBrains Mono', monospace; font-size: 24px; font-weight: 600; color: #0f172a; line-height: 1; }
        .efb-stat-unit { font-size: 14px; font-weight: 400; color: #94a3b8; }
        .efb-stat-sub  { font-size: 12px; color: #94a3b8; margin-top: 5px; }
        .efb-stat.sky   .efb-stat-val { color: #2563eb; }
        .efb-stat.green .efb-stat-val { color: #15803d; }
        .efb-stat.amber .efb-stat-val { color: #b45309; }
        .efb-stat.red   .efb-stat-val { color: #dc2626; }

        /* ── COUNTDOWN ── */
        .efb-clock {
          background: #fff; border: 1px solid #e2e8f0; border-radius: 12px;
          padding: 16px 20px; display: flex; align-items: center; gap: 18px;
          margin-bottom: 18px; flex-wrap: wrap;
        }
        .efb-clock-label { font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #94a3b8; margin-bottom: 4px; }
        .efb-clock-val { font-family: 'JetBrains Mono', monospace; font-size: 18px; font-weight: 600; color: #2563eb; }
        .efb-clock-val.warn    { color: #b45309; }
        .efb-clock-val.expired { color: #dc2626; }
        .efb-progress-wrap { flex: 1; min-width: 80px; height: 6px; background: #f1f5f9; border-radius: 3px; overflow: hidden; }
        .efb-progress-bar  { height: 100%; border-radius: 3px; transition: width 1s linear, background 0.4s; }
        .efb-clock-flt { font-family: 'JetBrains Mono', monospace; font-size: 13px; color: #94a3b8; white-space: nowrap; }

        /* ── LEGS ── */
        .efb-legs-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 22px; margin-bottom: 18px; }
        .efb-legs-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
        .efb-add-btn {
          font-size: 13px; font-weight: 600; font-family: 'Manrope', sans-serif;
          background: #eff6ff; border: 1.5px solid #bfdbfe; color: #2563eb;
          padding: 7px 14px; border-radius: 8px; cursor: pointer; transition: background 0.15s;
        }
        .efb-add-btn:hover { background: #dbeafe; }

        .efb-leg-rows { display: flex; flex-direction: column; gap: 12px; }

        /* Each leg row is a card with two zones: airports | times */
        .efb-leg-row {
          border: 1.5px solid #e2e8f0; border-radius: 10px; overflow: hidden;
          transition: border-color 0.15s;
        }
        .efb-leg-row:hover  { border-color: #cbd5e1; }
        .efb-leg-row.illegal { border-color: #fca5a5; }

        /* Top strip: leg number + airport codes */
        .efb-leg-top {
          display: grid;
          grid-template-columns: 44px 1fr 100px 1fr 90px 1fr 32px;
          gap: 10px; align-items: center;
          padding: 10px 14px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }
        .efb-leg-row.illegal .efb-leg-top { background: #fff5f5; border-bottom-color: #fecaca; }

        /* Bottom strip: dep time | arr time | block | turn | bar */
        .efb-leg-bottom {
          display: grid;
          grid-template-columns: 1fr 120px 1fr 80px 60px;
          gap: 12px; align-items: center;
          padding: 10px 14px;
        }

        .efb-leg-num {
          font-family: 'JetBrains Mono', monospace;
          font-size: 11px; font-weight: 700; color: #94a3b8; letter-spacing: 0.05em;
        }

        /* Airport code input */
        .efb-airport-group { display: flex; flex-direction: column; gap: 3px; }
        .efb-airport-label { font-size: 9px; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: #94a3b8; }
        .efb-airport-input {
          background: #fff; border: 1.5px solid #e2e8f0; color: #0f172a;
          padding: 7px 10px; border-radius: 7px;
          font-size: 14px; font-family: 'JetBrains Mono', monospace; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.06em;
          outline: none; width: 100%;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .efb-airport-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px #dbeafe; }
        .efb-airport-input.error { border-color: #fca5a5; background: #fff5f5; }

        /* Small tag under airport input */
        .efb-airport-tag {
          font-size: 10px; color: #64748b; white-space: nowrap;
          overflow: hidden; text-overflow: ellipsis; max-width: 100%;
        }
        .efb-airport-tag.error { color: #dc2626; }

        /* Arrow between airport boxes */
        .efb-arrow { font-size: 14px; color: #cbd5e1; text-align: center; user-select: none; }

        /* Time input group */
        .efb-time-group { display: flex; flex-direction: column; gap: 4px; }
        .efb-time-label { font-size: 10px; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: #94a3b8; }
        .efb-time-input {
          background: #fff; border: 1.5px solid #e2e8f0; color: #0f172a;
          padding: 9px 12px; border-radius: 8px;
          font-size: 16px; font-family: 'JetBrains Mono', monospace; font-weight: 500;
          width: 100%; outline: none; transition: border-color 0.15s, box-shadow 0.15s;
        }
        .efb-time-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px #dbeafe; }
        .efb-time-input.calculated { border-color: #bfdbfe; background: #eff6ff; color: #1d4ed8; }
        .efb-time-input.illegal { border-color: #fca5a5; background: #fff5f5; color: #dc2626; }
        /* UTC sub-label under time input */
        .efb-time-utc { font-size: 10px; color: #94a3b8; font-family: 'JetBrains Mono', monospace; margin-top: 2px; }

        /* Derived value */
        .efb-derived-group { display: flex; flex-direction: column; gap: 4px; }
        .efb-derived-label { font-size: 10px; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: #94a3b8; }
        .efb-derived-val { font-family: 'JetBrains Mono', monospace; font-size: 15px; font-weight: 500; color: #334155; padding: 9px 0; }
        .efb-derived-val.illegal { color: #dc2626; }
        .efb-derived-val.muted   { color: #94a3b8; }

        /* Bar */
        .efb-bar-wrap { height: 5px; background: #e2e8f0; border-radius: 3px; overflow: hidden; align-self: center; }
        .efb-bar { height: 100%; border-radius: 3px; background: #60a5fa; transition: width 0.4s; }
        .efb-bar.illegal { background: #fca5a5; }

        /* Remove button */
        .efb-remove {
          background: none; border: none; color: #cbd5e1; cursor: pointer;
          font-size: 16px; padding: 4px; border-radius: 4px; line-height: 1;
          transition: color 0.15s, background 0.15s;
        }
        .efb-remove:hover    { color: #ef4444; background: #fff1f2; }
        .efb-remove:disabled { opacity: 0.2; cursor: not-allowed; }

        /* Mobile: stack bottom zone — dep | block / arr | turn / bar hidden */
        @media (max-width: 600px) {
          .efb-leg-top    { grid-template-columns: 44px 1fr 24px 1fr 32px; }
          .efb-leg-bottom { grid-template-columns: 1fr 1fr; grid-template-rows: auto auto; }
          .efb-leg-bottom > *:nth-child(1) { grid-column: 1; grid-row: 1; }
          .efb-leg-bottom > *:nth-child(2) { grid-column: 2; grid-row: 1; }
          .efb-leg-bottom > *:nth-child(3) { grid-column: 1; grid-row: 2; }
          .efb-leg-bottom > *:nth-child(4) { grid-column: 2; grid-row: 2; }
          .efb-leg-bottom > *:nth-child(5) { display: none; }
        }

        /* ── ALERTS ── */
        .efb-alerts { display: flex; flex-direction: column; gap: 10px; }
        .efb-alert {
          display: flex; align-items: flex-start; gap: 10px;
          padding: 12px 16px; border-radius: 10px;
          font-size: 14px; font-weight: 500; line-height: 1.5;
        }
        .efb-alert-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; margin-top: 4px; }
        .efb-alert.ok     { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .efb-alert.ok     .efb-alert-dot { background: #22c55e; }
        .efb-alert.warn   { background: #fffbeb; color: #92400e; border: 1px solid #fde68a; }
        .efb-alert.warn   .efb-alert-dot { background: #f59e0b; }
        .efb-alert.danger { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
        .efb-alert.danger .efb-alert-dot { background: #ef4444; }

        /* ── DARK MODE (.dark class — Tailwind class-based) ──────────────── */

        /* Page */
        .dark .efb { background: #0f172a; color: #e2e8f0; }
        .dark .efb-header h1 { color: #f1f5f9; }
        .dark .efb-header p  { color: #94a3b8; }

        /* Status badges */
        .dark .efb-badge.legal   { background: #052e16; color: #4ade80; border-color: #166534; }
        .dark .efb-badge.illegal { background: #450a0a; color: #f87171; border-color: #991b1b; }
        .dark .efb-badge.unset   { background: #1e293b; color: #94a3b8; border-color: #334155; }

        /* Cards */
        .dark .efb-card { background: #1e293b; border-color: #334155; }
        .dark .efb-card-title { color: #64748b; }
        .dark .efb-hint { color: #64748b; }
        .dark .efb-acclim-note { background: #1c1108; color: #fbbf24; border-color: #78350f; }

        /* Form controls */
        .dark .efb-control { background: #0f172a; border-color: #334155; color: #e2e8f0; }
        .dark .efb-control:focus { background: #1e293b; border-color: #2563eb; box-shadow: 0 0 0 3px #172554; }

        /* Crew toggle */
        .dark .efb-crew-toggle { border-color: #334155; }
        .dark .efb-crew-btn { color: #94a3b8; }
        .dark .efb-crew-btn:first-child { border-right-color: #334155; }
        .dark .efb-crew-btn.active { background: #2563eb; color: #fff; }

        /* Summary table */
        .dark .efb-summary-table tr { border-top-color: #1e293b; }
        .dark .efb-summary-table td:first-child { color: #94a3b8; }
        .dark .efb-summary-table td:last-child  { color: #e2e8f0; }
        .dark .efb-summary-table td.sky   { color: #60a5fa; }
        .dark .efb-summary-table td.green { color: #4ade80; }
        .dark .efb-summary-table td.red   { color: #f87171; }

        /* Stat cards */
        .dark .efb-stat { background: #1e293b; border-color: #334155; }
        .dark .efb-stat-val { color: #f1f5f9; }
        .dark .efb-stat-label,
        .dark .efb-stat-unit,
        .dark .efb-stat-sub  { color: #64748b; }
        .dark .efb-stat.sky   .efb-stat-val { color: #60a5fa; }
        .dark .efb-stat.green .efb-stat-val { color: #4ade80; }
        .dark .efb-stat.amber .efb-stat-val { color: #fbbf24; }
        .dark .efb-stat.red   .efb-stat-val { color: #f87171; }

        /* Countdown */
        .dark .efb-clock { background: #1e293b; border-color: #334155; }
        .dark .efb-clock-label { color: #64748b; }
        .dark .efb-clock-val   { color: #60a5fa; }
        .dark .efb-clock-val.warn    { color: #fbbf24; }
        .dark .efb-clock-val.expired { color: #f87171; }
        .dark .efb-progress-wrap { background: #334155; }
        .dark .efb-clock-flt { color: #64748b; }

        /* Add leg button */
        .dark .efb-add-btn { background: #172554; border-color: #1e3a8a; color: #60a5fa; }
        .dark .efb-add-btn:hover { background: #103756; }

        /* Legs card */
        .dark .efb-legs-card { background: #1e293b; border-color: #334155; }

        /* Leg rows */
        .dark .efb-leg-row { border-color: #334155; }
        .dark .efb-leg-row:hover { border-color: #475569; }
        .dark .efb-leg-row.illegal { border-color: #991b1b; }
        .dark .efb-leg-top { background: #0f172a; border-bottom-color: #334155; }
        .dark .efb-leg-row.illegal .efb-leg-top { background: #2d0a0a; border-bottom-color: #991b1b; }
        .dark .efb-leg-num { color: #64748b; }

        /* Airport inputs */
        .dark .efb-airport-label { color: #64748b; }
        .dark .efb-airport-input { background: #1e293b; border-color: #334155; color: #e2e8f0; }
        .dark .efb-airport-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px #172554; }
        .dark .efb-airport-input.error { border-color: #991b1b; background: #2d0a0a; }
        .dark .efb-airport-tag { color: #94a3b8; }
        .dark .efb-airport-tag.error { color: #f87171; }
        .dark .efb-arrow { color: #475569; }

        /* Time inputs */
        .dark .efb-time-label { color: #64748b; }
        .dark .efb-time-input { background: #1e293b; border-color: #334155; color: #e2e8f0; }
        .dark .efb-time-input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px #172554; }
        .dark .efb-time-input.illegal { border-color: #991b1b; background: #2d0a0a; color: #f87171; }
        .dark .efb-time-input.calculated { border-color: #1e3a8a; background: #172554; color: #60a5fa; }
        .dark .efb-time-utc { color: #64748b; }
        .dark .efb-calc-label { color: #60a5fa; }

        /* Derived values */
        .dark .efb-derived-label { color: #64748b; }
        .dark .efb-derived-val { color: #cbd5e1; }
        .dark .efb-derived-val.illegal { color: #f87171; }
        .dark .efb-derived-val.muted   { color: #475569; }

        /* Bar */
        .dark .efb-bar-wrap { background: #334155; }

        /* Remove button */
        .dark .efb-remove { color: #475569; }
        .dark .efb-remove:hover { color: #f87171; background: #2d0a0a; }

        /* Acclimated timezone select */
        .dark .efb-control option,
        .dark .efb-field select option { background: #1e293b; color: #e2e8f0; }

        /* Alerts */
        .dark .efb-alert.ok     { background: #052e16; color: #4ade80; border-color: #166534; }
        .dark .efb-alert.ok     .efb-alert-dot { background: #22c55e; }
        .dark .efb-alert.warn   { background: #1c1108; color: #fbbf24; border-color: #78350f; }
        .dark .efb-alert.warn   .efb-alert-dot { background: #f59e0b; }
        .dark .efb-alert.danger { background: #450a0a; color: #fca5a5; border-color: #991b1b; }
        .dark .efb-alert.danger .efb-alert-dot { background: #ef4444; }
      `}</style>

      <div className="efb">
        {/* ── HEADER ── */}
        <div className="efb-header">
          <div>
            <h1>FAR 117 FDP Analyzer</h1>
            <p>Flight duty period limits · FAR Part 117 · 24-hr local time per airport</p>
          </div>
          <span className={`efb-badge ${!hasResult ? "unset" : isLegal ? "legal" : "illegal"}`}>
            {!hasResult ? "Awaiting Input" : isLegal ? "Legal" : "Illegal"}
          </span>
        </div>

        <div className="efb-layout">

          {/* ══ LEFT SIDEBAR ══ */}
          <div>
            <div className="efb-card">
              <div className="efb-card-title">Duty Parameters</div>
              <div className="efb-field">
                <label>Report date</label>
                <input
                  type="date"
                  className="efb-control"
                  value={dutyDate}
                  onChange={handleDutyDateChange}
                />
              </div>
              <div className="efb-field">
                <label>Report time (local 24hr)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="HH:MM"
                  maxLength={5}
                  className="efb-control efb-control-mono"
                  value={dutyTime}
                  onChange={handleDutyTimeChange}
                />
                <span className="efb-hint">
                  FDP clock starts here
                  {hasResult && reportOffsetLabel ? ` · ${reportOffsetLabel}` : ""}
                </span>
              </div>
              <div className="efb-field">
                <label>Flight crew</label>
                <div className="efb-crew-toggle">
                  <button className={`efb-crew-btn${pilots === 2 ? " active" : ""}`} onClick={() => setPilots(2)}>
                    2 Pilots
                  </button>
                  <button className={`efb-crew-btn${pilots === 3 ? " active" : ""}`} onClick={() => setPilots(3)}>
                    3 Pilots (Aug)
                  </button>
                </div>
              </div>
            </div>

            {/* Acclimated timezone selector */}
            <div className="efb-card" style={{ marginTop: 16 }}>
              <div className="efb-card-title">Acclimated Timezone</div>
              <div className="efb-field" style={{ marginBottom: 0 }}>
                <label>Home / acclimated base</label>
                <select
                  className="efb-control"
                  value={acclimatedZone}
                  onChange={(e) => setAcclimatedZone(e.target.value)}
                >
                  <option value="">Use departure airport timezone</option>
                  {ACCLIMATED_ZONES.map(z => (
                    <option key={z.id} value={z.id}>{z.label}</option>
                  ))}
                </select>
                <span className="efb-hint">
                  {acclimatedZone
                    ? `FAR 117 Table B/A lookup uses ${acclimatedOffsetLabel ?? acclimatedZone}`
                    : "FAR 117 Table B/A lookup uses dep airport timezone"}
                </span>
                {acclimatedZone && (
                  <div className="efb-acclim-note">
                    Crew must have spent ≥ 72 hrs in this timezone to be acclimated per FAR 117.3
                  </div>
                )}
              </div>
            </div>

            {hasResult && (
              <div className="efb-card">
                <div className="efb-card-title">FDP Summary</div>
                <table className="efb-summary-table">
                  <tbody>
                    {[
                      ["Report time",    `${reportLocal}${reportOffsetLabel ? ` (${reportOffsetLabel})` : ""}`, "sky"],
                      ...(acclimatedZoneLabel ? [["Acclimated tz", `${acclimatedZoneLabel} (${acclimatedOffsetLabel})`, ""]] : []),
                      ["FDP limit",      `${result?.limits.fdpLimit ?? previewData?.fdpLimit ?? "--"} hrs`,   isLegal ? "green" : "red"],
                      ["FDP end",        `${result ? fmt24(result.limits.dutyEndLimit, baseOffset) : previewData ? fmt24(previewData.dutyEnd, previewData.depOffset) : "--"} (${reportOffsetLabel || fmtOffset(previewData?.depOffset) || "UTC"})`, "sky"],
                      ["Latest T/O",     (() => { if (result?.latestTakeoff && !isNaN(result.latestTakeoff)) { return fmt24(result.latestTakeoff, segments[segments.length - 1]?.depAirport?.utc_offset_hours ?? baseOffset); } return "--"; })(), ""],
                      ["Flt time limit", `${result.limits.flightLimit} hrs`, ""],
                      ["Total flt time", fmtMins(result.totalFlightTime),   ""],
                    ].map(([label, val, cls]) => (
                      <tr key={label}>
                        <td>{label}</td>
                        <td className={cls}>{val}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ══ RIGHT CONTENT ══ */}
          <div className="efb-results">
            {/* Stat cards */}
            <div className="efb-stats">
              <div className={`efb-stat ${hasResult ? (isLegal ? "green" : "red") : previewData ? "sky" : ""}`}>
                <div className="efb-stat-label">FDP Limit</div>
                <div className="efb-stat-val">
                  {result?.limits.fdpLimit ?? previewData?.fdpLimit ?? "--"}
                  <span className="efb-stat-unit">{(result || previewData) ? " hr" : ""}</span>
                </div>
                <div className="efb-stat-sub">
                  {validDutyStart ? `${segments.length} leg${segments.length !== 1 ? "s" : ""}` : "—"}
                </div>
              </div>

              <div className="efb-stat sky">
                <div className="efb-stat-label">FDP End</div>
                <div className="efb-stat-val" style={{ fontSize: 20 }}>
                  {result
                    ? fmt24(result.limits.dutyEndLimit, baseOffset)
                    : previewData
                      ? fmt24(previewData.dutyEnd, previewData.depOffset)
                      : "--"}
                </div>
                <div className="efb-stat-sub">
                  {validDutyStart ? `Report ${reportLocal}` : "—"}
                </div>
              </div>

              <div className="efb-stat sky">
                <div className="efb-stat-label">Latest T/O</div>
                <div className="efb-stat-val" style={{ fontSize: 20 }}>
                  {(() => {
                    // Use engine result if available
                    if (result?.latestTakeoff && !isNaN(result.latestTakeoff)) {
                      const ltoOffset = segments[segments.length - 1]?.depAirport?.utc_offset_hours ?? baseOffset;
                      return fmt24(result.latestTakeoff, ltoOffset);
                    }
                    // Fallback: derive from blockInput + FDP limit when dep/arr not yet entered.
                    // Only compute when we have a real offset (acclimated zone or dep airport).
                    const lastSeg = segments[segments.length - 1];
                    const bi = lastSeg?.blockInput;
                    const hasRealOffset = Boolean(acclimatedZone) || lastSeg?.depAirport?.utc_offset_hours != null;
                    if (validDutyStart && bi && bi.length === 5 && bi.includes(":") && hasRealOffset) {
                      const [bh, bm] = bi.split(":").map(Number);
                      const blockMs = (bh * 60 + bm) * 60_000;
                      // Reuse previewData's offset if available (same priority logic)
                      const depOffset = lastSeg?.depAirport?.utc_offset_hours ?? (acclimatedZone
                        ? getTimezoneOffset(acclimatedZone, dutyStartInput.split("T")[0])
                        : 0);
                      const dutyLocalMs = validDutyStart.getTime() + depOffset * 3_600_000;
                      const dutyLocal = new Date(dutyLocalMs);
                      // Import getTableB logic inline for the fallback
                      const t = dutyLocal.getUTCHours() * 100 + dutyLocal.getUTCMinutes();
                      const TABLE_B_ROWS = [
                        {s:0,   e:359,  v:[9,9,9,9,9,9,9]},
                        {s:400, e:459,  v:[10,10,10,10,9,9,9]},
                        {s:500, e:559,  v:[12,12,12,12,11.5,11,10.5]},
                        {s:600, e:659,  v:[13,13,12,12,11.5,11,10.5]},
                        {s:700, e:1159, v:[14,14,13,13,12.5,12,11.5]},
                        {s:1200,e:1259, v:[13,13,13,13,12.5,12,11.5]},
                        {s:1300,e:1659, v:[12,12,12,12,11.5,11,10.5]},
                        {s:1700,e:2159, v:[12,12,11,11,10,9,9]},
                        {s:2200,e:2259, v:[11,11,10,10,9,9,9]},
                        {s:2300,e:2359, v:[10,10,10,9,9,9,9]},
                      ];
                      const row = TABLE_B_ROWS.find(r => t >= r.s && t <= r.e);
                      const fdpHrs = row ? row.v[Math.min(segments.length, 7) - 1] : 9;
                      const dutyEndUTC = new Date(validDutyStart.getTime() + fdpHrs * 3_600_000);
                      const ltoUTC = new Date(dutyEndUTC.getTime() - blockMs);
                      const ltoOffset = lastSeg?.depAirport?.utc_offset_hours ?? depOffset;
                      return fmt24(ltoUTC, ltoOffset);
                    }
                    return "--";
                  })()}
                </div>
                <div className="efb-stat-sub">last leg · local</div>
              </div>

              <div className={`efb-stat ${margin === null ? "" : margin < 0 ? "red" : margin < 30 ? "amber" : "green"}`}>
                <div className="efb-stat-label">Margin</div>
                <div className="efb-stat-val">{margin !== null ? fmtMins(Math.abs(margin)) : "--"}</div>
                <div className="efb-stat-sub">
                  {margin !== null ? (margin < 0 ? "over limit" : "to FDP end") : "—"}
                </div>
              </div>
            </div>

            {/* Countdown */}
            {validDutyStart && result && (
              <div className="efb-clock">
                <div style={{ minWidth: 180 }}>
                  <div className="efb-clock-label">FDP Countdown</div>
                  <div className={`efb-clock-val${clock?.expired ? " expired" : (clock?.remainingMin ?? 999) < 30 ? " warn" : ""}`}>
                    {clock
                      ? clock.expired
                        ? `Expired ${fmtMins(Math.abs(clock.remainingMin))} ago`
                        : `${fmtMins(clock.remainingMin)} remaining`
                      : "--"}
                  </div>
                </div>
                <div className="efb-progress-wrap">
                  <div className="efb-progress-bar" style={{
                    width: `${clockPct}%`,
                    background: clock?.expired ? "#ef4444" : (clock?.remainingMin ?? 999) < 30 ? "#f59e0b" : "#60a5fa",
                  }} />
                </div>
                <div className="efb-clock-flt">Flt limit: {result.limits.flightLimit} hrs</div>
              </div>
            )}

            {/* ── LEG ROWS ── */}
            <div className="efb-legs-card">
              <div className="efb-legs-header">
                <div className="efb-card-title" style={{ margin: 0 }}>Legs</div>
                <button className="efb-add-btn" onClick={addLeg}>+ Add Leg</button>
              </div>

              <div className="efb-leg-rows">
                {segments.map((seg, i) => {
                  const leg       = result?.timeline[i];
                  // Only mark illegal when the engine produced a real result for this leg
                  // (blockMin NaN means dep/arr were empty when engine ran)
                  const isIllegal = leg && !leg.legal && leg.blockMin != null && !isNaN(leg.blockMin);
                  const blockMin  = leg?.blockMin ?? null;
                  const turn      = turnMins[i];
                  const barPct    = blockMin ? Math.round((blockMin / maxBlockMin) * 100) : 0;

                  // UTC equivalents for display under local times
                  const depUTCStr = leg ? utcToLocal(leg.depUTC, 0) : null;
                  const arrUTCStr = leg ? utcToLocal(leg.arrUTC, 0) : null;
                  const showUTC   = seg.depAirport || seg.arrAirport; // only show UTC row when tz info present

                  return (
                    <div key={i} className={`efb-leg-row${isIllegal ? " illegal" : ""}`}>

                      {/* TOP: airports */}
                      <div className="efb-leg-top">
                        <span className="efb-leg-num">L{i + 1}</span>

                        {/* Dep airport */}
                        <div className="efb-airport-group">
                          <span className="efb-airport-label">From</span>
                          <input
                            type="text"
                            maxLength={4}
                            placeholder="ICAO / IATA"
                            className={`efb-airport-input${seg.depError ? " error" : ""}`}
                            value={seg.depCode}
                            onChange={(e) => updateSegment(i, "depCode", e.target.value.toUpperCase())}
                            onBlur={(e)   => handleAirportBlur(i, "depCode", e.target.value)}
                          />
                          <AirportTag airport={seg.depAirport} error={seg.depError} />
                        </div>

                        <span className="efb-arrow">→</span>

                        {/* Arr airport */}
                        <div className="efb-airport-group">
                          <span className="efb-airport-label">To</span>
                          <input
                            type="text"
                            maxLength={4}
                            placeholder="ICAO / IATA"
                            className={`efb-airport-input${seg.arrError ? " error" : ""}`}
                            value={seg.arrCode}
                            onChange={(e) => updateSegment(i, "arrCode", e.target.value.toUpperCase())}
                            onBlur={(e)   => handleAirportBlur(i, "arrCode", e.target.value)}
                          />
                          <AirportTag airport={seg.arrAirport} error={seg.arrError} />
                        </div>

                        {/* Spacer + remove */}
                        <div />
                        <button
                          className="efb-remove"
                          onClick={() => removeLeg(i)}
                          disabled={segments.length <= 1}
                          title="Remove leg"
                        >✕</button>
                      </div>

                      {/* BOTTOM: dep | block | arr | turn | bar */}
                      <div className="efb-leg-bottom">
                        {/* Departure time */}
                        <div className="efb-time-group">
                          <span className="efb-time-label">
                            Departure{seg.depAirport ? ` · ${fmtOffset(seg.depAirport.utc_offset_hours)}` : ""}
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="HH:MM"
                            maxLength={5}
                            className={`efb-time-input${isIllegal ? " illegal" : ""}${seg.depCalc ? " calculated" : ""}`}
                            value={seg.dep}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^0-9:]/g, "").slice(0, 5);
                              if (v.length === 2 && !v.includes(":")) v = v + ":";
                              updateSegment(i, "dep", v);
                            }}
                          />
                          {seg.depCalc && <span className="efb-calc-label">calculated</span>}
                          {!seg.depCalc && showUTC && depUTCStr && (
                            <span className="efb-time-utc">UTC {depUTCStr}</span>
                          )}
                        </div>

                        {/* Block input — editable, drives solver */}
                        <div className="efb-time-group">
                          <span className="efb-time-label">Block · UTC</span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="HH:MM"
                            maxLength={5}
                            className={`efb-time-input${isIllegal ? " illegal" : ""}`}
                            value={seg.blockInput}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^0-9:]/g, "").slice(0, 5);
                              if (v.length === 2 && !v.includes(":")) v = v + ":";
                              updateSegment(i, "blockInput", v);
                            }}
                          />

                        </div>

                        {/* Arrival time */}
                        <div className="efb-time-group">
                          <span className="efb-time-label">
                            Arrival{seg.arrAirport ? ` · ${fmtOffset(seg.arrAirport.utc_offset_hours)}` : ""}
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="HH:MM"
                            maxLength={5}
                            className={`efb-time-input${isIllegal ? " illegal" : ""}${seg.arrCalc ? " calculated" : ""}`}
                            value={seg.arr}
                            onChange={(e) => {
                              let v = e.target.value.replace(/[^0-9:]/g, "").slice(0, 5);
                              if (v.length === 2 && !v.includes(":")) v = v + ":";
                              updateSegment(i, "arr", v);
                            }}
                          />
                          {seg.arrCalc && <span className="efb-calc-label">calculated</span>}
                          {!seg.arrCalc && showUTC && arrUTCStr && (
                            <span className="efb-time-utc">UTC {arrUTCStr}</span>
                          )}
                        </div>

                        {/* Turn */}
                        <div className="efb-derived-group">
                          <span className="efb-derived-label">Turn</span>
                          <span className={`efb-derived-val${turn == null ? " muted" : ""}`}>
                            {turn != null ? fmtMins(turn) : "—"}
                          </span>
                        </div>

                        {/* Bar */}
                        <div className="efb-bar-wrap">
                          <div className={`efb-bar${isIllegal ? " illegal" : ""}`} style={{ width: `${barPct}%` }} />
                        </div>
                      </div>

                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── ALERTS ── */}
            {hasResult && (
              <div className="efb-alerts">
                {isLegal ? (
                  <div className="efb-alert ok">
                    <div className="efb-alert-dot" />
                    All legs legal within FDP limits.
                  </div>
                ) : (
                  result.timeline.filter((l) => !l.legal).map((l) => (
                    <div key={l.leg} className="efb-alert danger">
                      <div className="efb-alert-dot" />
                      Leg {l.leg} arrives {l.arrLocal} local ({fmt24(l.arrUTC, 0)} UTC) — exceeds FDP end {fmt24(result.limits.dutyEndLimit, baseOffset)} local.
                    </div>
                  ))
                )}
                {critical && isLegal && (
                  <div className="efb-alert warn">
                    <div className="efb-alert-dot" />
                    Leg {critical.leg} is the critical leg — first to bust with additional delay.
                  </div>
                )}
                {margin !== null && margin >= 0 && margin < 30 && (
                  <div className="efb-alert warn">
                    <div className="efb-alert-dot" />
                    Only {fmtMins(margin)} of margin remaining before FDP bust.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ══ CUMULATIVE LIMITS & REST (117.23, 117.25) from the logbook ══ */}
          <div className="efb-tw efb-limits">
            <Far117LimitsCard />
          </div>
        </div>
      </div>
    </>
  );
}